import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	GetProjectCatalogGraphDocument,
	GetProjectRepositoriesDocument,
	LinkProjectRepositoryDocument,
	type LinkProjectRepositoryMutationVariables,
	UnlinkProjectRepositoryDocument,
} from "@/common/artemis-sdk/graphql";
import type { CatalogGraph, ProjectRepository } from "../model";
import { useGqlClient } from "../use-gql-client";

/** Repositories linked to a project via its workspace's git provider connection (REQ-090). */
export function useProjectRepositories(projectId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["projectRepositories", projectId],
		queryFn: async () => {
			const data = await gqlFetch(GetProjectRepositoriesDocument, {
				projectId,
			});
			return (data.projectRepositories as ProjectRepository[]) ?? [];
		},
		enabled: !!projectId,
	});
}

/** How long a fetched catalog graph is reused before the tab re-asks the API. */
const CATALOG_GRAPH_STALE_MS = 5 * 60_000;

/**
 * The project's dependency graph, merged from each linked repository's Backstage
 * `catalog-info.yaml`. The API reads the files from the git provider and caches the result
 * briefly, so this is neither refetched on window focus nor while fresh; keyed under the
 * repository list so linking or unlinking refreshes it too. `useRefreshProjectCatalogGraph`
 * forces a re-read of the files.
 */
export function useProjectCatalogGraph(projectId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["projectRepositories", projectId, "catalogGraph"],
		queryFn: async () => {
			const data = await gqlFetch(GetProjectCatalogGraphDocument, {
				projectId,
			});
			return data.projectCatalogGraph as CatalogGraph;
		},
		enabled: !!projectId,
		staleTime: CATALOG_GRAPH_STALE_MS,
		refetchOnWindowFocus: false,
	});
}

/** Re-reads the project's catalog files, bypassing the API's cache, and replaces the cached graph. */
export function useRefreshProjectCatalogGraph(projectId: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async () => {
			const data = await gqlFetch(GetProjectCatalogGraphDocument, {
				projectId,
				refresh: true,
			});
			return data.projectCatalogGraph as CatalogGraph;
		},
		onSuccess: (graph) => {
			queryClient.setQueryData(
				["projectRepositories", projectId, "catalogGraph"],
				graph,
			);
		},
	});
}

/** Links a repository to a project. Invalidates that project's repository list on success. */
export function useLinkProjectRepository() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (input: LinkProjectRepositoryMutationVariables) => {
			const data = await gqlFetch(LinkProjectRepositoryDocument, input);
			return data.linkProjectRepository.repository;
		},
		onSuccess: (_repository, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["projectRepositories", variables.projectId],
			});
		},
	});
}

/** Unlinks a repository from a project. Invalidates the project's repository list on success. */
export function useUnlinkProjectRepository(projectId: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (projectRepositoryId: string) => {
			const data = await gqlFetch(UnlinkProjectRepositoryDocument, {
				projectRepositoryId,
			});
			return data.unlinkProjectRepository.success;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["projectRepositories", projectId],
			});
		},
	});
}
